import React, { useEffect, useMemo, useState } from 'react';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { LinearProgress, SnackbarContent } from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import { Button, createTheme, ThemeProvider, Tooltip, Typography, Grid, Box, Paper, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Field, reduxForm } from 'redux-form';
import PreviewIcon from '@mui/icons-material/Preview';
import SearchIcon from '@mui/icons-material/Search';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { connect, useSelector } from 'react-redux';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import renderSelectField from 'src/shared/components/form/Select';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OrderDetailsShippingViewDetailsModal from '../OrderHistory/components/OrderDetailsShippingViewDetailsModal';
import OrderReturnView from './OrderReturnView';
import OrderRemarksDetailsModel from '../OrderHistory/components/OrderRemarksDetailsModel';
import { Redirect } from 'react-router-dom';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import Badge from "react-bootstrap/Badge";
import renderMuiDateTimePickerField from 'src/shared/components/form/MuiDatePicker';

// Compact Input Field Renderer
const renderCompactField = ({ input, placeholder, type, meta: { touched, error, warning }, disabled, icon: Icon, children }) => (
  <div>
    <div className={`input-group input-group-sm ${touched && error ? 'has-validation' : ''}`}>
      {Icon && (
        <span className="input-group-text bg-light border-end-0 text-muted" style={{ padding: '0.25rem 0.5rem' }}>
          <Icon style={{ fontSize: '1rem' }} />
        </span>
      )}
      <input
        {...input}
        className={`form-control form-control-sm ${touched && error ? 'is-invalid' : ''} ${Icon ? 'border-start-0' : ''}`}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        style={{ fontSize: '0.85rem', height: '32px' }}
      />
      {children}
      {touched && ((error && <div className="invalid-feedback">{error}</div>) || (warning && <div className="text-warning small">{warning}</div>))}
    </div>
  </div>
);

const theme = createTheme({
  typography: {
    fontFamily: 'ProductSansLight, sans-serif',
    h3: {
      fontFamily: 'ProductSansBold, sans-serif',
      fontSize: 24,
      fontWeight: 600,
      marginBottom: '4px'
    },
    subtitle1: {
      fontFamily: 'ProductSansLight, sans-serif',
      fontSize: 14,
      color: '#64748b'
    },
    h6: {
      fontSize: 16,
    }
  },
  palette: {
    primary: {
      main: '#0ea5e9', // Sky blue
    },
    secondary: {
      main: '#64748b', // Slate
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '6px 16px',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1) !important'
        }
      }
    },
  }
});

const OrderReturnViewIndex = ({ formValues, reset }) => {
  const orderPreferences = useSelector(state => state.orderPreferences);
  useEffect(() => {
    getAllRidersDetails();
    getMerchants();
  }, [])

  const [reactTableData, setReactTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(null);
  const [temp, setTempData] = useState("");
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [viewRemarksPopUp, setRemarksPopUp] = useState(false);
  const [isTitle, setTitle] = useState(null);
  const [isOrderIdRemarks, setOrderIdRemarks] = useState(null);
  const [isConfirtmation, setShowConfirmation] = useState(false);
  const [isOrderId, setOrderId] = useState(null);
  const [isRedirect, setIsRedirect] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: (row, i) => i + 1,
        disableGlobalFilter: true,
        disableSortBy: true,
        width: 15,
        Cell: ({ row }) => <span style={{ fontWeight: 600, color: '#64748b' }}>{row.index + 1}</span>,
      },
      {
        Header: 'Action',
        accessor: 'edit',
        width: 100,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            {!row.original.returnedStatus ? (
              <Tooltip title="Please Click to Return the Order" arrow>
                <IconButton 
                  size="small" 
                  onClick={() => showConfirmation(row.original)} 
                  color="error" 
                  sx={{ bgcolor: '#fee2e2' }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title="Click here to View the Order Details" arrow>
              <IconButton 
                size="small" 
                onClick={() => viewDetails(row.original)} 
                color="primary" 
                sx={{ bgcolor: '#e0f2fe' }}
              >
                <PreviewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
      {
        Header: "Order Type",
        accessor: "orderType",
        Cell: ({ row }) => (
          <>
            {row.original.orderType === 1 ? (
              <Badge bg="success" className="px-2 py-1">DROP OFF</Badge>
            ) : row.original.orderType === 2 ? (
              <Badge bg="primary" className="px-2 py-1">PICK UP</Badge>
            ) : (
              <Badge bg="warning" className="px-2 py-1 text-dark">PnD</Badge>
            )}
          </>
        )
      },
      {
        Header: 'Order No.',
        accessor: 'orderNo',
        Cell: ({ value }) => <span style={{ fontWeight: 600 }}>{value}</span>
      },
      // {
      //   Header: 'Invoice No.',
      //   accessor: 'invoiceNo',
      //   disableGlobalFilter: false,
      // },
      {
        Header: 'ERPNo.',
        accessor: 'erpOrderNo',
        disableGlobalFilter: false,
      },
      {
        Header: 'Customer Name',
        accessor: 'name',
        disableGlobalFilter: false,
      },
      {
        Header: "Order Status",
        accessor: "orderStatusInfo",
        Cell: ({ row }) => (
          <div>
            <Badge bg={row.original.returnedStatus ? "warning" : "success"} text={row.original.returnedStatus ? "dark" : "light"} className="px-2 py-1 border">
              {row.original.returnedStatus ? `${row.original?.orderStatusInfo?.orderStatusDes} - Order Return` : row.original?.orderStatusInfo?.orderStatusDes}
            </Badge>
          </div>
        ),
      },
      {
        Header: 'Remark',
        accessor: 'remarksview',
        width: 80,
        Cell: row => (
          <div className="d-flex align-items-center">
            <Tooltip title="View Remarks" arrow>
              <IconButton 
                size="small" 
                onClick={() => viewRemarks(row.row.original)} 
                color="secondary" 
                sx={{ bgcolor: '#f1f5f9' }}
              >
                <CommentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [],
  );

  const showConfirmation = async (event) => {
    if (event?.orderId) {
      setOrderId(event.orderId);
      setShowConfirmation(true);
    }
  }

  const getAllRidersDetails = async () => {
    try {
      setLoading(true);
      const response = await PndRestApi.GetAllRidersDetails();
      if (response.status === 200 && response.data.status === 200) {
        // Riders are not directly needed in filters for this screen, but keeping API call as requested
      } else {
        console.error('fetching data:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchBasedOnFilters = async () => {
    try {
      let orderFromDate = formValues?.orderFromDate;
      let orderToDate = formValues?.orderToDate;
      let orderType = formValues?.orderType?.value;
      let orderStatus = formValues?.orderStatus?.value;
      let orderNo = formValues?.orderNo;

      const hasAnyFilter = Boolean(
        orderFromDate ||
        orderToDate ||
        (orderType !== undefined && orderType !== null && orderType !== '') ||
        (orderStatus !== undefined && orderStatus !== null && orderStatus !== '') ||
        (orderNo && String(orderNo).trim() !== '')
      );

      if (!hasAnyFilter) {
        showNotification(
          "theme-light",
          "ltr",
          "Warning",
          "Please select at least one filter (e.g. Date Range, Order No, Status) to search."
        );
        return;
      }

      setLoading(true);
      setReactTableData([]);

      let Obj = {
        orderFromDate: orderFromDate,
        orderToDate: orderToDate,
        orderType: orderType,
        isReturn: true,
        orderStatus: orderStatus,
        orderNo: orderNo ? String(orderNo).trim() : orderNo,
      };

      const response = await PndRestApi.GetOrderHistory(Obj);
      if (response.status === 200 && response.data.status === 200) {
        setReactTableData({ tableHeaderData: columns, tableRowsData: response.data.data });
        setNoRecordFound(true);
      } else if (response.status && response?.data?.status === 204) {
        setNoRecordFound(false);
        setReactTableData({ tableHeaderData: columns, tableRowsData: null });
      }
    } catch (error) {
      console.log("error", error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  if (isRedirect) {
    return <Redirect to="/web_app/pnd_order/order_details" />;
  }

  const viewDetails = async (event) => {
    if (event?.orderId) {
      setTempData(event);
      setViewDetailPopup(true);
      setTitle("View Order Details")
    }
  }

  const viewRemarks = async (event) => {
    if (event?.orderId) {
      setOrderIdRemarks(event?.orderId);
      setRemarksPopUp(true);
      setTitle("Order Remarks")
    }
  }

  const clearFilters = () => {
    reset();
  };

  const getMerchants = async () => {
    try {
      const response = await PndRestApi.GetAllMerchantHubMappingDetails();
      if (response.status === 200 && response.data.status === 200) {
        // Merchants are not directly needed in filters for this screen, but keeping API call as requested
      } else {
        console.error('Unexpected response status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await PndRestApi.ReturnOrderStatusUpdate(isOrderId);
      if (response.status === 200 && response.data.status === 200) {
        successNofication(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message: Return Request Created successfully.`,
        );
        setIsRedirect(true);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.log("error", error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setShowConfirmation(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="p-3" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

        {/* Header Section */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <Typography variant="h3" sx={{ color: '#1e293b' }}>Order Return Details</Typography>
            <Typography variant="subtitle1">View and manage all order returns.</Typography>
          </div>
        </Box>

        {/* Filters Section - Compact Accordion */}
        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          sx={{ mb: 2, borderRadius: '12px !important', '&:before': { display: 'none' }, border: '1px solid #e2e8f0', boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              minHeight: '48px',
              '& .MuiAccordionSummary-content': { margin: '12px 0' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: '#0ea5e9', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#334155' }}>Search Filters</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order No.</label>
                    <Field
                      name="orderNo"
                      component={renderCompactField}
                      type="text"
                      placeholder="Type Order No."
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order From Date</label>
                    <Field
                      name="orderFromDate"
                      component={renderMuiDateTimePickerField}
                      fullWidth
                      size="small"
                      margin="none"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order To Date</label>
                    <Field
                      name="orderToDate"
                      component={renderMuiDateTimePickerField}
                      fullWidth
                      size="small"
                      margin="none"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={3}>
                  <div className="mb-1">
                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order Type</label>
                    <Field
                      name="orderType"
                      component={renderSelectField}
                      options={[
                        { value: 0, label: "All" },
                        orderPreferences?.data?.dropOffEnabled && { value: 1, label: "Drop Off" },
                        orderPreferences?.data?.pickupEnabled && { value: 2, label: "Pick Up" },
                        orderPreferences?.data?.pickAndDropEnabled && { value: 3, label: "Pick & Drop" }
                      ].filter(Boolean)}
                      placeholder="Select Type"
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mt: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearFilters}
                    startIcon={<ClearAllIcon fontSize="small" />}
                    size="small"
                    sx={{ borderColor: '#cbd5e1', color: '#64748b' }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={searchBasedOnFilters}
                    startIcon={<SearchIcon fontSize="small" />}
                    size="small"
                    sx={{ color: '#fff' }}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>
          </AccordionDetails>
        </Accordion>

        {/* Results Section */}
        <Box sx={{ mt: 1 }}>
          {loading && <LinearProgress sx={{ borderRadius: 4, mb: 1 }} />}

          {isNoRecordFound !== null && !isNoRecordFound && (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
              <Typography variant="body2" color="textSecondary">No records found matching your criteria.</Typography>
            </Paper>
          )}

          {(reactTableData && reactTableData?.tableRowsData) && isNoRecordFound && (
            <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <OrderReturnView reactTableData={reactTableData} isNoRecordFound={isNoRecordFound} />
            </Paper>
          )}
        </Box>

        {viewDetailPopup && (
          <OrderDetailsShippingViewDetailsModal
            modalShow={viewDetailPopup}
            setModalShow={setViewDetailPopup}
            dataInfo={temp}
            isTitle={isTitle}
          />
        )}

        {isConfirtmation && (
          <ConfirmationDialog
            open={isConfirtmation}
            onClose={closeDialog}
            onConfirm={handleSubmit}
            title="Confirmation"
            message="Are you sure you want to return this order?"
          />
        )}

        {viewRemarksPopUp && (
          <OrderRemarksDetailsModel
            modalShow={viewRemarksPopUp}
            isOrderId={isOrderIdRemarks}
            setModalShow={setRemarksPopUp}
            isTitle={isTitle}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => ({
  formValues: state.form.OrderReturnViewIndexForm ? state.form.OrderReturnViewIndexForm.values : {},
});

export default connect(mapStateToProps)(reduxForm({ form: 'OrderReturnViewIndexForm', enableReinitialize: true })(OrderReturnViewIndex));